import { Component, inject } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { TAGPermission } from '../../../models/tagpermission';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TAGEvent } from '../../../models/tagevent';
import { ToggleButtonModule } from 'primeng/togglebutton';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';

export interface EventEditDialogData {
  event: TAGEvent;
}

@Component({
  selector: 'app-internal-event-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    ToggleButtonModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './eventEditDialog.html',
  styleUrl: './eventEditDialog.scss',
})
export class EventEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private api = inject(ApiService);

  eventForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    isPublic: new FormControl(false),
    location: new FormControl(''),
    contact: new FormControl(''),
    contactMail: new FormControl('', [Validators.email]),
    startdate: new FormControl(new Date()),
    enddate: new FormControl(new Date()),
    equipment: new FormControl(''),
    description: new FormControl(''),
  });

  eventIsNew: boolean = true;
  event: TAGEvent;

  constructor(
    public dialogRef: DynamicDialogRef<EventEditDialog>,
    public config: DynamicDialogConfig<EventEditDialogData>,
  ) {
    if (config.data?.event) {
      this.event = new TAGEvent(Object.assign({}, config.data.event.serialize()));
      this.eventForm.patchValue(this.event);
      this.eventIsNew = false;
    } else {
      this.event = new TAGEvent();
      this.eventForm.reset();
    }

    if (this.event._id) {
      config.header = this.translate.translate('internal.events.editTitle', this.event)();
    } else {
      config.header = this.translate.translate('internal.events.editTitleNew')();
    }
  }

  hasPerm(permission: string): boolean {
    return this.api.user().hasPermission(TAGPermission.fromName(permission));
  }

  deleteEvent() {
    if (this.eventIsNew) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.events.deleteTitle', {
          event: this.event,
        })(),
        this.translate.translate('internal.events.deleteMessage', {
          event: this.event,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.events.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteEvent(this.event._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  closeDialog(save?: boolean) {
    if (!save) {
      this.dialogRef.close(false);
      return;
    }

    if (!this.eventForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate("internal.events.updateProgress")()),
    );

    if (this.eventIsNew) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate("internal.events.createProgress")()),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      this.event.deserialize(this.eventForm.value);

      if (this.eventIsNew) {
        this.api
          .newEvent(this.event)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateEvent(this.event)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }
}
